// YERİ: src/app/core/company/demo-lens.service.ts
//
// Deneme merceği — kiracının bir sayfayı ÖRNEK veriyle görmesi.
//
// ── NASIL ÇALIŞIR ─────────────────────────────────────────────────────────
// Üst bardaki düğme → `POST /portal/demo/ensure` (sektör için demo şirket
// yoksa oluşturup doldurur) → dönen demoCompanyId `X-Demo-Lens` başlığıyla
// her isteğe eklenir (auth.interceptor). Sunucu o isteği demo şirket
// kapsamında koşturur. Aktif şirket DEĞİŞMEZ: seçici, para birimi, ad aynı
// kalır; yalnız `activeCompanyId()` demo Id'yi döner ki store'lar ve
// CompanyRouteWatcher (sayfa sıçratma) kendiliğinden tazelensin.
//
// ── NEDEN SAYFA BAZLI ─────────────────────────────────────────────────────
// Rota değişince mercek KAPANIR. Açık kalsaydı bir sayfada örnek, ötekinde
// gerçek veri görünür ve kullanıcı hangisinin gerçek olduğunu kaybederdi.
// Sıçrama rotası (/portal/_reload, /portal) rota değişimi SAYILMAZ.

import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';
import { API_BASE } from '@/app/core/auth/auth.service';
import { NotificationStore } from '@/app/core/notifications/notification.store';
import { PermissionsService } from '@/app/core/permissions/permissions.service';
import { ActiveCompanyService } from './active-company.service';

export interface DemoCompanyDto {
  demoCompanyId: number;
  sectorKey: string;
  name: string;
  seededAt: string | null;
  sourceCompanyId: number | null;
  sectorSeederExists: boolean;
}

export interface DemoStatusDto {
  companies: DemoCompanyDto[];
  activeSectorKey: string | null;
  activeDemoCompanyId: number | null;
  supportedSectors: string[];
}

interface DemoEnsureResult {
  demoCompanyId: number;
  sectorKey: string;
  created: boolean;
  seeded: boolean;
  rowCount: number;
}

interface ApiResult<T> { success: boolean; message?: string; data: T; }

/** Sunucu çeviri ANAHTARI döner; ekranda gösterilecek karşılığı burada. */
const ERROR_KEYS: Record<string, string> = {
  'demo.error.no_sector':   'demo.err.noSector',
  'demo.error.no_company':  'demo.err.noCompany',
  'demo.error.seed_failed': 'demo.err.seedFailed',
  'demo.error.not_found':   'demo.err.notFound',
};

const BOUNCE_PATHS = new Set(['/portal/_reload', '/portal']);

@Injectable({ providedIn: 'root' })
export class DemoLensService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private company = inject(ActiveCompanyService);
  private permissions = inject(PermissionsService);
  private notify = inject(NotificationStore);
  private transloco = inject(TranslocoService);

  /** Mercek açık mı. */
  readonly active = computed(() => this.company.demoLens() !== null);
  /** Oluşturma/doldurma sürüyor (ilk açılışta 2-3 sn). */
  readonly busy = signal(false);
  readonly status = signal<DemoStatusDto | null>(null);
  readonly statusLoading = signal(false);

  constructor() {
    // Rota değişince kapat. NavigationEnd sıçrama rotalarında da gelir —
    // onlar mercek açılırken CompanyRouteWatcher'ın kendi yeniden yüklemesi.
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(e => {
      const lens = this.company.demoLens();
      if (!lens) return;
      const path = (e as NavigationEnd).urlAfterRedirects.split('?')[0];
      if (BOUNCE_PATHS.has(path) || path === lens.path) return;
      this.company.setDemoLens(null);
      this.permissions.refresh(this.company.activeCompany()?.id ?? null);
    });
  }

  toggle(): void {
    if (this.busy()) return;
    if (this.active()) { this.off(); return; }
    void this.on();
  }

  off(): void {
    if (!this.company.demoLens()) return;
    this.company.setDemoLens(null);
    // Yetkiler gerçek şirkete döner; loading kenarı sayfayı yeniden kurar.
    this.permissions.refresh(this.company.activeCompany()?.id ?? null);
  }

  private async on(): Promise<void> {
    this.busy.set(true);
    try {
      const res = await this.http.post<ApiResult<DemoEnsureResult>>(`${API_BASE}/portal/demo/ensure`, {}).toPromise();
      if (!res?.success || !res.data) {
        this.notify.error(this.errorText(res?.message));
        return;
      }
      const path = this.router.url.split('?')[0];
      this.company.setDemoLens({ demoCompanyId: res.data.demoCompanyId, sectorKey: res.data.sectorKey, path });
      // Yetkiler demo (=kaynak) şirket için tazelenir; loading true→false
      // kenarı CompanyRouteWatcher'ı tetikler ve sayfa örnek veriyle kurulur.
      this.permissions.refresh(res.data.demoCompanyId);
      this.notify.success(this.t(res.data.created ? 'demo.toast.created' : 'demo.toast.on'));
    } catch (err: unknown) {
      const msg = (err as { error?: { message?: string } })?.error?.message;
      this.notify.error(this.errorText(msg));
    } finally {
      this.busy.set(false);
    }
  }

  // ── Ayarlar sayfası ──────────────────────────────────────────────────────

  loadStatus(): void {
    this.statusLoading.set(true);
    this.http.get<ApiResult<DemoStatusDto>>(`${API_BASE}/portal/demo/status`).subscribe({
      next: r => { this.status.set(r.data ?? null); this.statusLoading.set(false); },
      error: () => this.statusLoading.set(false),
    });
  }

  async ensure(sectorKey: string | null, reset: boolean): Promise<boolean> {
    this.busy.set(true);
    try {
      const res = await this.http.post<ApiResult<DemoEnsureResult>>(`${API_BASE}/portal/demo/ensure`, { sectorKey, reset }).toPromise();
      if (!res?.success) { this.notify.error(this.errorText(res?.message)); return false; }
      // Yenilenen demonun Id'si değişti: açık mercek eskiyi taşıyorsa kapat.
      if (reset && this.company.demoLens()) this.off();
      this.notify.success(this.t(reset ? 'demo.toast.reset' : 'demo.toast.created'));
      this.loadStatus();
      return true;
    } catch (err: unknown) {
      this.notify.error(this.errorText((err as { error?: { message?: string } })?.error?.message));
      return false;
    } finally {
      this.busy.set(false);
    }
  }

  async remove(demoCompanyId: number): Promise<boolean> {
    this.busy.set(true);
    try {
      const res = await this.http.delete<ApiResult<unknown>>(`${API_BASE}/portal/demo/${demoCompanyId}`).toPromise();
      if (!res?.success) { this.notify.error(this.errorText(res?.message)); return false; }
      if (this.company.demoLens()?.demoCompanyId === demoCompanyId) this.off();
      this.notify.success(this.t('demo.toast.deleted'));
      this.loadStatus();
      return true;
    } catch (err: unknown) {
      this.notify.error(this.errorText((err as { error?: { message?: string } })?.error?.message));
      return false;
    } finally {
      this.busy.set(false);
    }
  }

  private errorText(serverKey?: string | null): string {
    const key = serverKey && ERROR_KEYS[serverKey] ? ERROR_KEYS[serverKey] : 'demo.err.generic';
    return this.t(key);
  }

  private t(key: string): string {
    return this.transloco.translate(key);
  }
}
